import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import {
  fetchFundingHistory,
  setFundingPage,
} from '../../redux/slices/walletSlice';
import FidelityPaymentService from '../../services/fidelityPaymentService';
import {
  RefreshCw,
  Download,
  ChevronLeft,
  ChevronRight,
  X,
  AlertCircle,
  Eye,
  Inbox,
  ArrowUpCircle,
} from 'lucide-react';

const FundingHistory: React.FC = () => {
  const dispatch = useDispatch();
  const {
    fundingTransactions,
    fundingPagination,
    loading,
    error,
  } = useSelector((state: RootState) => state.wallet);
  const [selectedTransaction, setSelectedTransaction] = useState<any | null>(null);

  useEffect(() => {
    dispatch(fetchFundingHistory({
      page: fundingPagination.page,
      limit: fundingPagination.limit,
    }) as any);
  }, [dispatch, fundingPagination.page, fundingPagination.limit]);

  const handlePageChange = (newPage: number) => {
    dispatch(setFundingPage(newPage));
  };

  const formatDate = (dateString: string | Date) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const formatDateTime = (dateString: string | Date) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getStatusBadgeClass = (status: string) => {
    const statusMap: { [key: string]: string } = {
      completed: 'badge-success',
      pending: 'badge-warning',
      failed: 'badge-danger',
    };
    return statusMap[status] || 'badge-info';
  };

  const capitalizeStatus = (status: string) =>
    status.charAt(0).toUpperCase() + status.slice(1);

  return (
    <div className="card overflow-hidden">
      {/* Table Header */}
      <div className="card-header flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-fintech-text">Funding History</h3>
          <p className="text-xs text-fintech-textMuted mt-0.5">
            {fundingPagination.total > 0
              ? `${fundingPagination.total} total transactions`
              : 'All your account funding transactions'}
          </p>
        </div>

        <button
          className="p-1.5 rounded-lg text-fintech-textMuted hover:bg-slate-100 transition-colors self-start sm:self-auto"
          onClick={() => dispatch(fetchFundingHistory({ page: fundingPagination.page, limit: fundingPagination.limit }) as any)}
          title="Refresh"
          aria-label="Refresh funding history"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div className="mx-6 mt-4">
          <div className="alert alert-error">
            <AlertCircle size={16} className="flex-shrink-0" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && fundingTransactions.length === 0 ? (
        <div className="p-8 text-center">
          <div className="spinner mx-auto mb-3" />
          <p className="text-sm text-fintech-textMuted">Loading funding history...</p>
        </div>
      ) : fundingTransactions.length === 0 ? (
        /* Empty State */
        <div className="p-12 text-center">
          <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <Inbox size={24} className="text-fintech-textMuted" />
          </div>
          <h3 className="text-sm font-semibold text-fintech-text mb-1">No funding transactions yet</h3>
          <p className="text-xs text-fintech-textMuted">
            When you fund your account, transactions will appear here.
          </p>
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th className="text-right">Amount</th>
                  <th className="text-right">Balance After</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {fundingTransactions.map((transaction) => (
                  <tr key={transaction.transactionId} >
                    <td className="text-sm text-fintech-textMuted whitespace-nowrap">
                      {formatDate(transaction.createdAt)}
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                          <ArrowUpCircle size={13} className="text-emerald-600" />
                        </div>
                        <span className="text-sm text-fintech-text truncate max-w-[180px]">
                          {transaction.description}
                        </span>
                      </div>
                    </td>
                    <td className="numeric font-semibold text-emerald-700">
                      +{formatCurrency(transaction.amount)}
                    </td>
                    <td className="numeric text-sm text-fintech-textMuted">
                      {formatCurrency(transaction.newBalance)}
                    </td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(transaction.status)}`}>
                        {capitalizeStatus(transaction.status)}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center justify-end">
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium text-fintech-secondary bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors"
                          onClick={() => setSelectedTransaction(transaction)}
                          title="View details"
                        >
                          <Eye size={11} />
                          Details
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {fundingPagination.pages > 1 && (
            <div className="px-6 py-4 border-t border-fintech-border flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-xs text-fintech-textMuted">
                Page <span className="font-semibold text-fintech-text">{fundingPagination.page}</span> of{' '}
                <span className="font-semibold text-fintech-text">{fundingPagination.pages}</span>
                {' '}· {fundingPagination.total} total
              </p>
              <div className="flex items-center gap-1">
                <button
                  className="p-1.5 rounded-lg text-fintech-textMuted hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  onClick={() => handlePageChange(fundingPagination.page - 1)}
                  disabled={fundingPagination.page === 1}
                  aria-label="Previous page"
                >
                  <ChevronLeft size={16} />
                </button>

                {Array.from({ length: Math.min(fundingPagination.pages, 5) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      className={`w-8 h-8 rounded-lg text-xs font-medium transition-colors ${
                        fundingPagination.page === page
                          ? 'bg-fintech-primary text-white'
                          : 'text-fintech-textMuted hover:bg-slate-100'
                      }`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  );
                })}

                <button
                  className="p-1.5 rounded-lg text-fintech-textMuted hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  onClick={() => handlePageChange(fundingPagination.page + 1)}
                  disabled={fundingPagination.page === fundingPagination.pages}
                  aria-label="Next page"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      {selectedTransaction && (
        <div
          className="modal-backdrop"
          onClick={() => setSelectedTransaction(null)}
        >
          <div
            className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-fintech-border">
              <h3 className="font-semibold text-fintech-text">Funding Details</h3>
              <button
                className="p-1.5 rounded-lg text-fintech-textMuted hover:bg-slate-100 transition-colors"
                onClick={() => setSelectedTransaction(null)}
                aria-label="Close details"
              >
                <X size={16} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-5 space-y-1">
              {[
                { label: 'Description', value: selectedTransaction.description },
                { label: 'Amount', value: formatCurrency(selectedTransaction.amount), bold: true, green: true },
                { label: 'Balance Before', value: formatCurrency(selectedTransaction.previousBalance) },
                { label: 'Balance After', value: formatCurrency(selectedTransaction.newBalance), bold: true },
                { label: 'Created', value: formatDateTime(selectedTransaction.createdAt) },
                {
                  label: 'Funding Ref',
                  value: selectedTransaction.transactionRef || selectedTransaction.fidelityPaymentId || '—',
                  mono: true,
                },
              ].map((row) => (
                <div key={row.label} className="flex items-start justify-between gap-4 py-2.5 border-b border-slate-50 last:border-0">
                  <span className="text-xs text-fintech-textMuted flex-shrink-0">{row.label}</span>
                  <span className={`text-xs text-right break-all ${row.mono ? 'font-mono text-fintech-textMuted' : ''} ${row.bold ? 'font-bold' : ''} ${row.green ? 'text-emerald-700' : 'text-fintech-text'}`}>
                    {row.value}
                  </span>
                </div>
              ))}

              {/* Status */}
              <div className="flex items-center justify-between py-2.5">
                <span className="text-xs text-fintech-textMuted">Status</span>
                <span className={`badge ${getStatusBadgeClass(selectedTransaction.status)}`}>
                  {capitalizeStatus(selectedTransaction.status)}
                </span>
              </div>
            </div>

            {/* Modal Footer */}
            {selectedTransaction.fidelityPaymentId && (
              <div className="px-6 pb-5">
                <button
                  type="button"
                  className="btn-secondary w-full justify-center text-sm"
                  onClick={() => FidelityPaymentService.downloadFundingReceipt(selectedTransaction.fidelityPaymentId)}
                >
                  <Download size={14} />
                  Download Receipt
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FundingHistory;
